import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Practical site for "Windows CLI Basics".
 *
 * It opens on a Windows desktop, not on a terminal. Finding the
 * Command Prompt is the first thing anybody has to do, and it is the
 * bit nobody teaches: a child who has only ever seen a black window
 * appear by magic cannot get one to appear on a machine of their own.
 * So the desktop comes first - Start opens, cmd is searched for, and a
 * real window appears, with a title bar, a minimise, and a close that
 * actually closes.
 *
 * Inside that window is a working prompt. Not a video of one, and not
 * a list of commands to copy: typed in, answered back, with a
 * filesystem underneath that can be walked around and searched.
 *
 * Everything it understands is read-only. Nothing creates, deletes or
 * changes anything, which is deliberate - the lesson is that looking at
 * a machine and altering one are different acts, and the looking is
 * where the skill is.
 *
 * Nine steps lead from "where am I?" to reading a file that has to be
 * found first, and it is genuinely hidden, so `dir` alone will not do
 * it. After the ninth the prompt stays open with a dozen more commands
 * to try.
 */

const STORE_KEY = 'cybercub.lab.windows-cli';

const USER = 'aarav';
const HOST = 'CUB-LAPTOP';

const BANNER = [
  'Microsoft Windows [Version 10.0.22631.3593]',
  '(c) Microsoft Corporation. All rights reserved.',
  '',
  'This prompt is a practice one. Nothing you type here can change',
  'anything. Type help to see what it understands.',
  '',
];

/* ------------------------------------------------------------------ */
/* the machine                                                        */
/* ------------------------------------------------------------------ */

/*
 * A folder is an object; a file is a string of its contents. `__hidden`
 * lists the names inside a folder that `dir` should not show unless it
 * is given /a.
 */
const DISK = {
  Users: {
    aarav: {
      Desktop: {
        'shortcut to game.lnk': 'This is a shortcut, not the game itself.',
      },
      Documents: {
        'homework.docx': 'Volcanoes: a report.\r\nBy Aarav, class 6B.',
        'notes.txt':
          'Remember: the Recycle Bin is not deletion.\r\nAsk Cub about the hidden folder.',
        '.private': {
          'cub-code.txt':
            'Well done for finding this.\r\n\r\nHiding a file is not security - it is a tick box.\r\nAnyone who types dir /a can see it, and so can anyone\r\nwho reads the disk directly.\r\n\r\nIf something really must stay private, it has to be\r\nencrypted, not hidden.',
        },
        __hidden: ['.private'],
      },
      Downloads: {
        'holiday.jpg': '(a picture - type cannot show it)',
        'invoice.pdf.exe':
          'MZ\u0090\u0000\u0003... (this is a program, not a document)',
      },
      // Both of these are genuinely hidden on a real Windows machine,
      // and they are here so step 3 has something to reveal. A /a that
      // changes nothing teaches nothing.
      AppData: { Local: {}, Roaming: {} },
      'NTUSER.DAT': '(the settings for this account - not text, and not readable this way)',
      __hidden: ['AppData', 'NTUSER.DAT'],
    },
  },
  Windows: {
    System32: {
      drivers: {},
      'notepad.exe': '(a program)',
    },
  },
  'Program Files': {},
};

/* ------------------------------------------------------------------ */
/* path handling                                                      */
/* ------------------------------------------------------------------ */

const isFolder = (node) => node !== null && typeof node === 'object';

/** Walk a list of folder names from the root. Returns null if lost. */
function nodeAt(parts) {
  let node = DISK;
  for (const part of parts) {
    if (!isFolder(node)) return null;
    const key = Object.keys(node).find(
      (k) => k !== '__hidden' && k.toLowerCase() === part.toLowerCase(),
    );
    if (key === undefined) return null;
    node = node[key];
  }
  return node;
}

const display = (parts) => `C:\\${parts.join('\\')}`;

/** Resolve whatever the user typed against where they are standing. */
function resolve(cwd, raw) {
  const arg = raw.replace(/^"|"$/g, '');
  let parts;
  if (/^[A-Za-z]:\\?/.test(arg)) parts = arg.slice(2).split('\\').filter(Boolean);
  else if (arg.startsWith('\\')) parts = arg.split('\\').filter(Boolean);
  else parts = [...cwd, ...arg.split('\\').filter(Boolean)];

  const out = [];
  for (const p of parts) {
    if (p === '.') continue;
    if (p === '..') out.pop();
    else out.push(p);
  }
  return out;
}

/** Every file under a folder, as full paths - for dir /s. */
function walk(parts, node, out = []) {
  if (!isFolder(node)) return out;
  for (const [name, child] of Object.entries(node)) {
    if (name === '__hidden') continue;
    const here = [...parts, name];
    if (isFolder(child)) walk(here, child, out);
    else out.push(display(here));
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* the commands                                                       */
/* ------------------------------------------------------------------ */

const pad = (s, n) => String(s).padEnd(n, ' ');

function listDir(parts, showHidden) {
  const node = nodeAt(parts);
  if (!isFolder(node)) return ['The system cannot find the path specified.'];

  const hidden = node.__hidden ?? [];
  const names = Object.keys(node)
    .filter((k) => k !== '__hidden')
    .filter((k) => showHidden || !hidden.includes(k));

  const lines = [` Directory of ${display(parts)}`, ''];
  for (const name of names) {
    const child = node[name];
    const tag = isFolder(child) ? '<DIR>         ' : pad(`${String(child).length}`, 14);
    const mark = hidden.includes(name) ? ' H' : '  ';
    lines.push(`16/09/2026  09:14    ${tag}${mark} ${name}`);
  }
  const files = names.filter((n) => !isFolder(node[n])).length;
  const dirs = names.length - files;
  lines.push('', `               ${files} File(s)`, `               ${dirs} Dir(s)`);
  return lines;
}

const NET = [
  'Windows IP Configuration',
  '',
  'Ethernet adapter Wi-Fi:',
  '',
  '   Connection-specific DNS Suffix  . : home',
  '   IPv4 Address. . . . . . . . . . . : 192.168.1.42',
  '   Subnet Mask . . . . . . . . . . . : 255.255.255.0',
  '   Default Gateway . . . . . . . . . : 192.168.1.1',
];

const COMMANDS = {
  help: () => [
    'Commands you can use here:',
    '',
    '  cd               show the folder you are in',
    '  cd <folder>      move into it   (cd .. goes back up)',
    '  dir              list this folder',
    '  dir /a           list it including hidden files',
    '  dir /s /b <name> search this folder and everything under it',
    '  tree             draw the folders as a shape',
    '  type <file>      print what is inside a file',
    '  whoami           which account am I?',
    '  hostname         what is this machine called?',
    '  ipconfig         what network is it on?',
    '  systeminfo       which Windows, and how patched?',
    '  tasklist         what is running',
    '  netstat -ano     what is listening on the network',
    '  net user         which accounts exist on this machine',
    '  cls              clear the screen',
    '  exit             close the window',
    '',
    'Nothing here changes anything. It only ever looks.',
  ],

  whoami: () => [`${HOST.toLowerCase()}\\${USER}`],
  hostname: () => [HOST],

  ipconfig: (args) =>
    args.includes('/all')
      ? [
          ...NET.slice(0, 4),
          '   Description . . . . . . . . . . . : Wireless-AC 9560',
          '   Physical Address. . . . . . . . . : 9C-B6-D0-1F-3A-77',
          ...NET.slice(4),
          '   DNS Servers . . . . . . . . . . . : 192.168.1.1',
        ]
      : NET,

  systeminfo: () => [
    `Host Name:                 ${HOST}`,
    'OS Name:                   Microsoft Windows 11 Home',
    'OS Version:                10.0.22631 N/A Build 22631',
    'System Type:               x64-based PC',
    'Total Physical Memory:     8,096 MB',
    'Hotfix(s):                 3 Hotfix(s) Installed.',
    '',
    'Note: three patches on a home machine is not many. On a real',
    'assessment this line is the first thing anybody reads.',
  ],

  tasklist: () => [
    'Image Name                     PID Session Name        Mem Usage',
    '========================= ======== ================ ============',
    'System                           4 Services              1,204 K',
    'explorer.exe                  4120 Console              62,880 K',
    'msedge.exe                    5992 Console             210,344 K',
    'notepad.exe                   6644 Console               9,012 K',
    'cmd.exe                       7108 Console               4,388 K',
  ],

  netstat: () => [
    'Active Connections',
    '',
    '  Proto  Local Address          Foreign Address        State           PID',
    '  TCP    0.0.0.0:135            0.0.0.0:0              LISTENING       956',
    '  TCP    0.0.0.0:445            0.0.0.0:0              LISTENING         4',
    '  TCP    192.168.1.42:51244     142.250.185.78:443     ESTABLISHED    5992',
    '',
    'LISTENING means something on this machine is waiting for a',
    'connection. Every one of those is a door worth asking about.',
  ],

  'net user': () => [
    `User accounts for \\\\${HOST}`,
    '',
    '-------------------------------------------------------------',
    'Administrator            aarav                    Guest',
    'The command completed successfully.',
  ],
};

/* ------------------------------------------------------------------ */
/* the nine steps                                                     */
/* ------------------------------------------------------------------ */

const STEPS = [
  {
    id: 's1',
    title: 'Where am I?',
    ask: 'Every prompt starts somewhere. Find out where.',
    hint: 'cd',
    done: (cmd) => /^cd\s*$/i.test(cmd),
  },
  {
    id: 's2',
    title: "What's around me?",
    ask: 'List what is in this folder.',
    hint: 'dir',
    done: (cmd) => /^dir\s*$/i.test(cmd),
  },
  {
    id: 's3',
    title: 'Are there hidden files?',
    ask: 'dir does not show everything in this folder. Ask it to.',
    hint: 'dir /a',
    done: (cmd) => /^dir\s+\/a\b/i.test(cmd),
  },
  {
    id: 's4',
    title: 'Moving around the filesystem',
    ask: 'Move into the Documents folder.',
    hint: 'cd Documents',
    done: (cmd, { cwd }) =>
      /^cd\s+\S/i.test(cmd) && cwd.join('\\').toLowerCase().endsWith('documents'),
  },
  {
    id: 's5',
    title: 'Finding the file on the disk',
    ask: 'Somewhere under here is a file called cub-code.txt. Search for it without knowing where it is.',
    hint: 'dir /s /b cub-code.txt',
    done: (cmd) => /^dir\b.*\/s\b/i.test(cmd) && /cub-code/i.test(cmd),
  },
  {
    id: 's6',
    title: 'Navigate to the file',
    ask: 'The search told you the folder. Go there.',
    hint: 'cd .private',
    done: (cmd, { cwd }) =>
      /^cd\s+\S/i.test(cmd) && cwd.join('\\').toLowerCase().endsWith('.private'),
  },
  {
    id: 's7',
    title: 'Read the file',
    ask: 'Print what is inside it.',
    hint: 'type cub-code.txt',
    done: (cmd) => /^type\s+.*cub-code\.txt/i.test(cmd),
  },
  {
    id: 's8',
    title: 'Name of the computer',
    ask: 'What is this machine actually called?',
    hint: 'hostname',
    done: (cmd) => /^hostname\s*$/i.test(cmd),
  },
  {
    id: 's9',
    title: 'How is this machine connected?',
    ask: 'Find its address on the network.',
    hint: 'ipconfig',
    done: (cmd) => /^ipconfig\b/i.test(cmd),
  },
];

/* ------------------------------------------------------------------ */
/* the desktop, drawn                                                 */
/* ------------------------------------------------------------------ */

/*
 * Drawn rather than screenshotted, for the same reason as the desktop
 * in the Windows Basics practical: a screenshot dates the moment
 * Windows changes its wallpaper, and a child on a different version
 * would be hunting for something that is not there. The shapes are what
 * have to be right - Start at one end of a bar along the bottom, the
 * clock at the other, icons up the left.
 */
const ICONS = [
  { kind: 'pc', label: 'This PC' },
  { kind: 'folder', label: 'My work' },
  // Windows wraps a long shortcut name onto a second line rather than
  // running it into the one beside it, so this does too.
  { kind: 'cmd', label: 'Command Prompt', lines: ['Command', 'Prompt'] },
  { kind: 'bin', label: 'Recycle Bin' },
];

function DesktopArt() {
  return (
    <svg viewBox="0 0 160 100" aria-hidden="true">
      <defs>
        <linearGradient id="cli-wall" x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0%" stopColor="#0f3f6b" />
          <stop offset="55%" stopColor="#123a63" />
          <stop offset="100%" stopColor="#0a1f3a" />
        </linearGradient>
        <radialGradient id="cli-bloom" cx="0.62" cy="0.34" r="0.55">
          <stop offset="0%" stopColor="#4aa3e8" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#4aa3e8" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="160" height="100" fill="url(#cli-wall)" />
      <ellipse cx="99" cy="34" rx="88" ry="55" fill="url(#cli-bloom)" />

      {/* ---------- desktop icons ---------- */}
      {ICONS.map((item, i) => {
        const y = 5 + i * 18;
        return (
          <g key={item.label} transform={`translate(5 ${y})`}>
            {item.kind === 'pc' && (
              <>
                <rect className="win-g-pc" x="0.6" y="0.5" width="7.8" height="5.4" rx="0.6" />
                <rect className="win-g-pc-screen" x="1.4" y="1.3" width="6.2" height="3.6" rx="0.3" />
                <rect className="win-g-pc" x="3" y="6.2" width="3" height="1" rx="0.3" />
              </>
            )}
            {item.kind === 'folder' && (
              <path className="win-g-folder" d="M0.6,1.2 h3.2 l0.9,1.2 h4.2 v4.6 h-8.3 z" />
            )}
            {item.kind === 'cmd' && (
              <>
                <rect className="cli-g-cmd" x="0.8" y="0.8" width="7.4" height="6" rx="0.6" />
                <rect className="cli-g-cmd-bar" x="0.8" y="0.8" width="7.4" height="1.4" rx="0.6" />
                <text className="cli-g-cmd-text" x="2.2" y="5.4">{'>_'}</text>
              </>
            )}
            {item.kind === 'bin' && (
              <>
                <path className="win-g-bin" d="M2,1.8 h5 l-0.6,5.2 h-3.8 z" />
                <rect className="win-g-bin" x="1.4" y="0.9" width="6.2" height="0.9" rx="0.4" />
              </>
            )}
            {(item.lines ?? [item.label]).map((t, li) => (
              <text key={t} className="win-icon-label" x="4.5" y={9.6 + li * 3.6}>{t}</text>
            ))}
          </g>
        );
      })}

      {/* ---------- taskbar ---------- */}
      <rect className="win-taskbar" x="0" y="90" width="160" height="10" />

      {/* start: the four-pane logo */}
      <g>
        <rect className="win-logo" x="5.2" y="93.4" width="1.6" height="1.6" />
        <rect className="win-logo" x="7.2" y="93.4" width="1.6" height="1.6" />
        <rect className="win-logo" x="5.2" y="95.4" width="1.6" height="1.6" />
        <rect className="win-logo" x="7.2" y="95.4" width="1.6" height="1.6" />
      </g>

      {/* search box */}
      <rect className="win-tb-search" x="12" y="92.8" width="24" height="4.6" rx="2.3" />
      <circle className="win-tb-magnifier" cx="14.6" cy="95.1" r="1" />
      <line className="win-tb-magnifier-handle" x1="15.3" y1="95.8" x2="16" y2="96.5" />
      <text className="win-tb-search-text" x="17.6" y="96.4">Search</text>

      {/* pinned apps - the folder one is File Explorer */}
      <path className="win-g-folder" d="M41,93.6 h2.2 l0.6,0.8 h3 v3.4 h-5.8 z" />
      <rect className="win-tb-app is-edge" x="51" y="93.2" width="4.4" height="4.4" rx="2.2" />
      <rect className="win-tb-app is-mail" x="58" y="93.4" width="4.6" height="3.8" rx="0.6" />

      {/* tray */}
      <path className="win-tray-wifi" d="M134,96.2 a3.4,3.4 0 0 1 4.6,0 M135.2,97.2 a1.8,1.8 0 0 1 2.2,0" />
      <path className="win-tray-speaker" d="M141.6,94.6 h1 l1.4,-1.2 v4.4 l-1.4,-1.2 h-1 z" />
      <text className="win-tray-clock" x="147" y="94.9">14:32</text>
      <text className="win-tray-clock cli-tray-date" x="147" y="98.2">12/09/2026</text>
    </svg>
  );
}

/* What Start shows before anything is typed. cmd is deliberately not
   among them: it is not pinned on a real machine either, and searching
   for it is the part worth learning. */
const PINNED = ['Edge', 'File Explorer', 'Settings', 'Notepad', 'Calculator', 'Photos'];

const WANTS_CMD = /^(cmd|cmd\.exe|command|command prompt|prompt|terminal)/i;

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (v) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* private window */ }
};

export default function WindowsCliLab() {
  const saved = useMemo(load, []);

  // 'closed' | 'open' | 'min' | 'max'
  const [win, setWin] = useState('closed');
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(false);

  const [cwd, setCwd] = useState(['Users', USER]);
  const [lines, setLines] = useState(BANNER);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  const [hPos, setHPos] = useState(-1);
  const [doneSteps, setDoneSteps] = useState(saved.done ?? []);
  const [showHint, setShowHint] = useState(false);

  const screenRef = useRef(null);
  const inputRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => { document.title = 'Windows CLI Basics · CyberCub Lab'; }, []);
  useEffect(() => { save({ done: doneSteps }); }, [doneSteps]);

  // Follow the output down, the way a real console does.
  useEffect(() => {
    const el = screenRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, win]);

  // Whatever has just appeared should be what the keyboard is pointed
  // at - the search box when Start opens, the prompt when it does.
  useEffect(() => { if (menu) searchRef.current?.focus(); }, [menu]);
  useEffect(() => {
    if (win === 'open' || win === 'max') inputRef.current?.focus();
  }, [win]);

  const showing = win === 'open' || win === 'max';
  const step = showing ? (STEPS.find((s) => !doneSteps.includes(s.id)) ?? null) : null;
  const allDone = doneSteps.length === STEPS.length;

  useFinished('/lab/windows-cli', allDone);

  const prompt = `${display(cwd)}>`;

  const openCmd = () => {
    setMenu(false);
    setSearch('');
    setPicked(false);
    setShowHint(false);
    setWin((w) => (w === 'max' ? 'max' : 'open'));
  };

  // A real close throws the buffer away and starts the next one in the
  // home folder. Steps already ticked stay ticked, but the place in the
  // filesystem is genuinely lost - because that is what closing a
  // window does.
  const closeCmd = () => {
    setWin('closed');
    setLines(BANNER);
    setCwd(['Users', USER]);
    setInput('');
    setHistory([]);
    setHPos(-1);
  };

  const run = (raw) => {
    const cmd = raw.trim();
    const out = [`${prompt}${raw}`];
    let nextCwd = cwd;

    if (!cmd) {
      setLines((l) => [...l, ...out]);
      return;
    }

    const lower = cmd.toLowerCase();
    const [verb, ...rest] = cmd.split(/\s+/);
    const v = verb.toLowerCase();

    if (v === 'cls') {
      setLines([]);
      setInput('');
      return;
    }
    if (v === 'exit') {
      closeCmd();
      return;
    }

    if (lower.startsWith('net user')) out.push(...COMMANDS['net user']());
    else if (v === 'help' || v === '?') out.push(...COMMANDS.help());
    else if (v === 'whoami') out.push(...COMMANDS.whoami());
    else if (v === 'hostname') out.push(...COMMANDS.hostname());
    else if (v === 'ipconfig') out.push(...COMMANDS.ipconfig(rest.map((r) => r.toLowerCase())));
    else if (v === 'systeminfo') out.push(...COMMANDS.systeminfo());
    else if (v === 'tasklist') out.push(...COMMANDS.tasklist());
    else if (v === 'netstat') out.push(...COMMANDS.netstat());
    else if (v === 'cd' || v === 'chdir') {
      const arg = rest.join(' ');
      if (!arg) out.push(display(cwd));
      else {
        const target = resolve(cwd, arg);
        if (isFolder(nodeAt(target))) nextCwd = target;
        else out.push('The system cannot find the path specified.');
      }
    } else if (v === 'dir') {
      const flags = rest.filter((r) => r.startsWith('/')).map((r) => r.toLowerCase());
      const term = rest.find((r) => !r.startsWith('/'));
      if (flags.includes('/s')) {
        const hits = walk(cwd, nodeAt(cwd)).filter(
          (p) => !term || p.toLowerCase().includes(term.toLowerCase().replace(/\*/g, '')),
        );
        out.push(...(hits.length ? hits : ['File Not Found']));
      } else {
        out.push(...listDir(cwd, flags.includes('/a')));
      }
    } else if (v === 'type') {
      const arg = rest.join(' ');
      const target = resolve(cwd, arg);
      const node = nodeAt(target);
      if (node === null) out.push('The system cannot find the file specified.');
      else if (isFolder(node)) out.push('Access is denied.');
      else out.push(...String(node).split(/\r?\n/));
    } else if (v === 'tree') {
      const draw = (parts, node, depth) => {
        if (!isFolder(node) || depth > 2) return;
        for (const name of Object.keys(node).filter((k) => k !== '__hidden')) {
          out.push(`${'   '.repeat(depth)}+--${name}`);
          draw([...parts, name], node[name], depth + 1);
        }
      };
      out.push(display(cwd));
      draw(cwd, nodeAt(cwd), 0);
    } else {
      out.push(
        `'${verb}' is not recognized as an internal or external command,`,
        'operable program or batch file.',
        '',
        'Type help to see what this prompt understands.',
      );
    }

    out.push('');
    setLines((l) => [...l, ...out]);
    setCwd(nextCwd);
    setHistory((h) => [cmd, ...h].slice(0, 40));
    setHPos(-1);
    setShowHint(false);

    // A step only counts if it is the one being asked for, and the
    // check is given the cwd *after* the command - "cd Documents" is
    // only right if it actually arrived there.
    if (step && step.done(cmd, { cwd: nextCwd })) {
      setDoneSteps((d) => [...d, step.id]);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter') {
      run(input);
      setInput('');
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const n = Math.min(hPos + 1, history.length - 1);
      if (n >= 0) { setHPos(n); setInput(history[n]); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const n = hPos - 1;
      setHPos(n);
      setInput(n >= 0 ? history[n] : '');
    }
  };

  /* ---- what the learner is being asked for right now ---- */
  const task = showing
    ? step && { title: step.title, ask: step.ask, hint: step.hint }
    : {
        title: win === 'min' ? 'Bring the window back' : 'Open the Command Prompt',
        ask:
          win === 'min'
            ? 'It has not closed — it is minimised, and its button is still on the taskbar.'
            : 'Click Start, type cmd, then open Command Prompt. Or double-click the Command Prompt icon on the desktop.',
        hint:
          win === 'min'
            ? 'Click the Command Prompt button on the taskbar'
            : 'Start ▸ type cmd ▸ Command Prompt',
      };

  const tutorSay = (() => {
    if (!showing) {
      return win === 'min'
        ? 'The window is only minimised, not closed. Its button is still on the taskbar - click that and it comes straight back, with everything you typed still in it.'
        : 'Before you can type a command you need somewhere to type it. Click Start, type cmd, and open the Command Prompt. That is how you get one on any Windows machine, and it is the bit nobody ever shows you.';
    }
    if (allDone) {
      return 'All nine. What you just did is the opening of nearly every real assessment: find out who you are, where you are, what is around you, and what the machine is. None of it changed a thing, and that is the point.';
    }
    if (!step) return 'Type help to see everything this prompt understands.';
    return `Step ${doneSteps.length + 1}. ${step.title}${/[?.!]$/.test(step.title) ? '' : '.'} ${step.ask} If you get stuck, press the hint button.`;
  })();

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>Windows CLI Basics</strong>
            <span className="lab-sub">Practical · Ethical Hacker, Module 2</span>
          </div>
        </div>
        <Link to="/learn/lesson/cf3-windows-basics" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <section className="lab-section">
        <div className="place-head">
          <div>
            <h2>Open a command prompt</h2>
            <p className="lab-lead">
              A Windows desktop, and a prompt you have to open yourself. Then type a command, press
              Enter, read the answer. Nothing here changes anything — every command only ever looks.
            </p>
          </div>
          <span className="pill">{doneSteps.length} / {STEPS.length} steps</span>
        </div>

        <Tutor avatar="cub-mint" say={tutorSay} />

        {/* ---------------- the nine steps ---------------- */}
        <ol className="cli-chips" aria-label="Steps">
          {STEPS.map((s, i) => {
            const done = doneSteps.includes(s.id);
            const now = step?.id === s.id;
            return (
              <li key={s.id} className={`${done ? 'is-done' : ''} ${now ? 'is-now' : ''}`}>
                <span className="cli-step-n">{done ? '✓' : i + 1}</span>
                <span className="cli-chip-label">{s.title}</span>
              </li>
            );
          })}
        </ol>

        {task && (
          <div className={`cli-task ${showing ? '' : 'is-opening'}`}>
            <div>
              <strong>{task.title}</strong>
              <p>{task.ask}</p>
            </div>
            {showHint ? (
              <code className="cli-hint">{task.hint}</code>
            ) : (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowHint(true)}>
                Show me
              </button>
            )}
          </div>
        )}

        {allDone && showing && (
          <div className="cli-task is-done">
            <div>
              <strong>All nine done.</strong>
              <p>
                The prompt stays open. Try <code>systeminfo</code>, <code>tasklist</code>,{' '}
                <code>netstat -ano</code>, <code>net user</code> or <code>tree</code>.
              </p>
            </div>
          </div>
        )}

        {/* ---------------- the desktop ---------------- */}
        <div className="win-desktop cli-desk">
          <DesktopArt />

          {/* the Command Prompt icon on the desktop. One click selects
              and two opens, as on a real desktop - but a single click
              is also offered, because a tablet has no double-click. */}
          <button
            type="button"
            className={`win-spot cli-icon-spot ${picked ? 'is-picked' : ''}`}
            style={{ left: '2.4%', top: '40%', width: '6.8%', height: '16%' }}
            onDoubleClick={openCmd}
            onClick={() => setPicked(true)}
            aria-label="Command Prompt on the desktop. Double-click to open."
          >
            {picked && <span className="cli-icon-open" onClick={openCmd}>Open</span>}
          </button>

          {/* Start, and the search box beside it */}
          <button
            type="button"
            className="win-spot cli-hot"
            style={{ left: '2.5%', top: '90.5%', width: '6.5%', height: '9%' }}
            onClick={() => setMenu((m) => !m)}
            aria-label="Start"
            aria-expanded={menu}
          />
          <button
            type="button"
            className="win-spot cli-hot"
            style={{ left: '7.5%', top: '92.5%', width: '15%', height: '5%' }}
            onClick={() => setMenu(true)}
            aria-label="Type here to search"
          />

          {/* the taskbar button, once there is something to show */}
          {win !== 'closed' && (
            <button
              type="button"
              className={`cli-tb-task ${showing ? 'is-active' : ''}`}
              onClick={() => setWin((w) => (w === 'min' ? 'open' : 'min'))}
              aria-label={win === 'min' ? 'Restore Command Prompt' : 'Minimise Command Prompt'}
            >
              <span className="cli-tb-tile">{'>_'}</span>
              <span className="cli-tb-name">Command Prompt</span>
            </button>
          )}

          {/* ---------------- Start ---------------- */}
          {menu && (
            <div className="cli-start" role="dialog" aria-label="Start">
              <input
                ref={searchRef}
                className="cli-start-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && WANTS_CMD.test(search.trim())) openCmd();
                  if (e.key === 'Escape') setMenu(false);
                }}
                placeholder="Type here to search"
                spellCheck="false"
                autoCapitalize="none"
                autoCorrect="off"
                aria-label="Search this machine"
              />

              {search.trim() === '' ? (
                <>
                  <p className="cli-start-head">Pinned</p>
                  <div className="cli-start-grid">
                    {PINNED.map((p) => (
                      <span key={p} className="cli-start-tile">{p}</span>
                    ))}
                  </div>
                  <p className="cli-start-foot">
                    Command Prompt is not pinned here — it is not pinned on a real machine either.
                    Type <code>cmd</code> to find it.
                  </p>
                </>
              ) : WANTS_CMD.test(search.trim()) ? (
                <>
                  <p className="cli-start-head">Best match</p>
                  <button type="button" className="cli-start-hit" onClick={openCmd}>
                    <span className="cli-tb-tile">{'>_'}</span>
                    <span className="cli-start-hit-text">
                      <strong>Command Prompt</strong>
                      <em>App</em>
                    </span>
                  </button>
                </>
              ) : (
                <p className="cli-start-none">
                  No results for “{search.trim()}”. Try <code>cmd</code>.
                </p>
              )}
            </div>
          )}

          {/* ---------------- the Command Prompt window ---------------- */}
          {showing && (
            <div className={`cli-win ${win === 'max' ? 'is-max' : ''}`}>
              <div className="cli-bar">
                <span className="cli-tb-tile is-sm">{'>_'}</span>
                <span className="cli-bar-title">Command Prompt — {display(cwd)}</span>
                <button type="button" className="cli-win-btn" onClick={() => setWin('min')} aria-label="Minimise">
                  &#8211;
                </button>
                <button
                  type="button"
                  className="cli-win-btn"
                  onClick={() => setWin((w) => (w === 'max' ? 'open' : 'max'))}
                  aria-label={win === 'max' ? 'Restore down' : 'Maximise'}
                >
                  &#9633;
                </button>
                <button type="button" className="cli-win-btn is-close" onClick={closeCmd} aria-label="Close">
                  &#10005;
                </button>
              </div>

              <div
                className="cli-screen"
                ref={screenRef}
                onClick={() => inputRef.current?.focus()}
                role="presentation"
              >
                {lines.map((l, i) => (
                  <div key={i} className="cli-line">{l || '\u00a0'}</div>
                ))}

                <div className="cli-line cli-entry">
                  <span className="cli-prompt">{prompt}</span>
                  <input
                    ref={inputRef}
                    className="cli-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKey}
                    spellCheck="false"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="off"
                    aria-label="Type a command"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {allDone && (
          <div className="lab-done">
            <div>
              <strong>That is the whole opening move.</strong>
              <p style={{ margin: '0.3rem 0 0' }}>
                Who am I, where am I, what is here, what is this machine, who can it reach. Five
                questions, none of which change anything — and the answers decide everything that
                could come next.
              </p>
            </div>
            <Link to="/learn/lesson/cf3-windows-basics" className="btn btn-sm">
              ← Back to the lesson
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
